import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Alert, Box, Drawer, IconButton, Typography } from '@mui/material';

export interface ProductSectionDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Drawer heading, e.g. "Prices" / "Barcodes". */
  title: string;
  /** One-line explanation under the heading. */
  subtitle?: string;
  /** Leading icon shown next to the heading. */
  icon?: ReactNode;
  /**
   * Pinned note under the scrollable content — used by the prices/stock drawers to explain that
   * their fields are part of the card draft and are persisted by the card's own Save button
   * (unlike barcodes/variants, which mutate immediately).
   */
  footnote?: string;
  children: ReactNode;
}

/**
 * Left-anchored slide-out panel for `ProductEditPage`'s secondary data blocks — prices, stock,
 * barcodes, variants (adr-merchant-portal-008). Deliberately `anchor="left"` (unlike the
 * right-anchored `VersionHistoryDrawer`): these panels are opened from the summary tiles on the
 * left-hand product card and slide out over the navigation side, keeping the card itself visible
 * on wide screens. Same paper sizing/header idiom as `VersionHistoryDrawer` otherwise, so the two
 * drawer families feel like one system.
 */
export function ProductSectionDrawer({
  open,
  onClose,
  title,
  subtitle,
  icon,
  footnote,
  children,
}: ProductSectionDrawerProps) {
  const { t } = useTranslation('catalog');

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      aria-label={title}
      slotProps={{
        paper: { sx: { width: { xs: '100vw', sm: 560 }, display: 'flex', flexDirection: 'column' } },
      }}
    >
      {/* ── Header (sticky) ─────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        {icon ? (
          <Box sx={{ display: 'inline-flex', color: 'action.active' }} aria-hidden>
            {icon}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.35 }} noWrap>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={t('common:action.close')}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Content (scrolls) ───────────────────────────────────────────── */}
      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>{children}</Box>

      {/* ── Pinned footnote ─────────────────────────────────────────────── */}
      {footnote ? (
        <Box sx={{ px: 2.5, pb: 2, flexShrink: 0 }}>
          <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
            {footnote}
          </Alert>
        </Box>
      ) : null}
    </Drawer>
  );
}
